import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, TrendingUp, Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { TrafficBadge } from "@/components/TrafficBadge";
import { DeltaChip } from "@/components/DeltaChip";
import { KPITrendChart } from "@/components/MemoAI/KPITrendChart";
import { AppShell } from "@/components/layout/AppShell";
import { z } from "zod";
import { sanitizeError } from "@/lib/errorUtils";

const submissionSchema = z.object({
  value: z.number().min(0),
  note: z.string().max(500).optional(),
  evidence_url: z.string().url().optional().or(z.literal("")),
});

interface KPI {
  id: string;
  name: string;
  unit: string;
  min_band: number | null;
  max_band: number | null;
  delta_warn: number | null;
  evidence_required: boolean;
  owner_user_id: string;
  company_id: string;
}

interface KPIValue {
  id: string;
  value: number;
  week_start_date: string;
  submitted_at: string;
  late: boolean;
  note: string | null;
  evidence_url: string | null;
}

const KPIDetail = () => {
  const { id, kpiId } = useParams();
  const navigate = useNavigate();
  const [kpi, setKpi] = useState<KPI | null>(null);
  const [values, setValues] = useState<KPIValue[]>([]);
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  
  const [form, setForm] = useState({
    value: "",
    note: "",
    evidence_url: "",
  });

  useEffect(() => {
    loadKPIData();
  }, [kpiId]);

  const loadKPIData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const [kpiRes, valuesRes, companyRes] = await Promise.all([
        supabase.from("kpis").select("*").eq("id", kpiId).single(),
        supabase
          .from("kpi_values")
          .select("*")
          .eq("kpi_id", kpiId)
          .order("week_start_date", { ascending: false })
          .limit(12),
        supabase.from("companies").select("name").eq("id", id).single(),
      ]);

      if (kpiRes.error) throw kpiRes.error;
      if (valuesRes.error) throw valuesRes.error;

      setKpi(kpiRes.data);
      setValues(valuesRes.data || []);
      setCompanyName(companyRes.data?.name || "");
      setIsOwner(kpiRes.data.owner_user_id === user.id);
    } catch (error: unknown) {
      toast.error(sanitizeError(error, "Failed to load KPI data"));
      navigate(`/app/companies/${id}`);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentWeekStart = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday.toISOString().split("T")[0];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kpi || !isOwner) return;
    
    setSubmitting(true);
    try {
      const validated = submissionSchema.parse({
        value: parseFloat(form.value),
        note: form.note || undefined,
        evidence_url: form.evidence_url || undefined,
      });

      if (kpi.evidence_required && !validated.evidence_url) {
        throw new Error("Evidence URL is required for this KPI");
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const weekStart = getCurrentWeekStart();

      const { data: existing } = await supabase
        .from("kpi_values")
        .select("id")
        .eq("kpi_id", kpiId)
        .eq("week_start_date", weekStart)
        .maybeSingle();

      if (existing) {
        throw new Error("Value already submitted for this week");
      }

      const { error } = await supabase.from("kpi_values").insert({
        kpi_id: kpiId!,
        week_start_date: weekStart,
        value: validated.value,
        note: validated.note || null,
        evidence_url: validated.evidence_url || null,
        submitted_by: user.id,
        late: false,
      });

      if (error) throw error;

      toast.success("Value submitted successfully");
      setForm({ value: "", note: "", evidence_url: "" });
      loadKPIData();
    } catch (err: unknown) {
      if (err instanceof z.ZodError) {
        toast.error(err.errors[0].message);
      } else {
        toast.error(sanitizeError(err, "Failed to submit value"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getTrafficStatus = (value: number): "red" | "amber" | "green" | "neutral" => {
    if (!kpi?.min_band && !kpi?.max_band) return "neutral";
    if (kpi.min_band && value < kpi.min_band) return "red";
    if (kpi.max_band && value > kpi.max_band) return "red";
    return "green";
  };

  const calculateDelta = (current: number, previous: number) => {
    return current - previous;
  };

  if (loading) {
    return (
      <AppShell title="Loading..." showBack backTo={`/app/companies/${id}`}>
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AppShell>
    );
  }

  if (!kpi) return null;

  const latestValue = values[0];
  const previousValue = values[1];

  // Prepare trend chart data
  const trendData = [...values].reverse().map(v => ({
    week: v.week_start_date,
    value: v.value,
  }));

  return (
    <AppShell
      title={kpi.name}
      subtitle={companyName}
      showBack
      backTo={`/app/companies/${id}`}
      companyId={id}
      companyName={companyName}
    >
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3 flex-wrap">
                <span className="truncate">{kpi.name}</span>
                {latestValue && <TrafficBadge status={getTrafficStatus(latestValue.value)} size="lg" />}
              </h1>
              <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                Target: {kpi.min_band && kpi.max_band ? `${kpi.min_band}–${kpi.max_band}${kpi.unit}` : "Not set"}
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate(`/app/companies/${id}/kpis/${kpiId}/edit`)}
              className="flex-shrink-0 rounded-xl"
            >
              <Settings className="h-4 w-4 mr-2" />
              Edit KPI
            </Button>
          </div>

          {/* Trend Chart */}
          {trendData.length > 1 && (
            <KPITrendChart
              title={kpi.name}
              unit={kpi.unit}
              data={trendData}
              minBand={kpi.min_band}
              maxBand={kpi.max_band}
            />
          )}

          {latestValue && (
            <Card className="rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Current value</CardTitle>
                <CardDescription>Week of {new Date(latestValue.week_start_date).toLocaleDateString()}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-3xl sm:text-4xl font-bold tabular-nums">
                      {latestValue.value.toLocaleString()}{kpi.unit}
                    </p>
                    {previousValue && (
                      <div className="mt-2">
                        <DeltaChip
                          delta={calculateDelta(latestValue.value, previousValue.value)}
                          unit={kpi.unit}
                        />
                      </div>
                    )}
                  </div>
                  {latestValue.note && (
                    <div>
                      <p className="text-sm font-medium mb-1">Note</p>
                      <p className="text-sm text-muted-foreground">{latestValue.note}</p>
                    </div>
                  )}
                  {latestValue.evidence_url && (
                    <div>
                      <p className="text-sm font-medium mb-1">Evidence</p>
                      <a
                        href={latestValue.evidence_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline break-all"
                      >
                        {latestValue.evidence_url}
                      </a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {isOwner && (
            <Card className="rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="h-5 w-5" />
                  Submit value for this week
                </CardTitle>
                <CardDescription>Week of {new Date(getCurrentWeekStart()).toLocaleDateString()}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="value">Value ({kpi.unit})</Label>
                    <Input
                      id="value"
                      type="number"
                      step="any"
                      placeholder="Enter value"
                      value={form.value}
                      onChange={(e) => setForm({ ...form, value: e.target.value })}
                      required
                      inputMode="decimal"
                      className="rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="note">Note (optional)</Label>
                    <Textarea
                      id="note"
                      placeholder="Add context or explanation..."
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      rows={3}
                      maxLength={500}
                      className="rounded-xl"
                    />
                  </div>

                  {kpi.evidence_required && (
                    <div className="space-y-2">
                      <Label htmlFor="evidence">Evidence URL *</Label>
                      <Input
                        id="evidence"
                        type="url"
                        placeholder="https://..."
                        value={form.evidence_url}
                        onChange={(e) => setForm({ ...form, evidence_url: e.target.value })}
                        required={kpi.evidence_required}
                        className="rounded-xl"
                      />
                    </div>
                  )}

                  <Button type="submit" disabled={submitting} className="w-full rounded-xl">
                    <Save className="h-4 w-4 mr-2" />
                    {submitting ? "Submitting..." : "Submit value"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          <Card className="rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">History</CardTitle>
              <CardDescription>Past submissions</CardDescription>
            </CardHeader>
            <CardContent>
              {values.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No values submitted yet</p>
              ) : (
                <div className="space-y-3">
                  {values.map((value, idx) => (
                    <div
                      key={value.id}
                      className="flex items-center justify-between p-3 sm:p-4 rounded-xl border border-border/50"
                    >
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                        <TrafficBadge status={getTrafficStatus(value.value)} />
                        <div className="min-w-0">
                          <p className="font-medium text-sm sm:text-base">
                            Week of {new Date(value.week_start_date).toLocaleDateString()}
                          </p>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            {new Date(value.submitted_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-base sm:text-lg font-semibold tabular-nums">
                          {value.value.toLocaleString()}{kpi.unit}
                        </p>
                        {idx < values.length - 1 && (
                          <DeltaChip
                            delta={calculateDelta(value.value, values[idx + 1].value)}
                            unit={kpi.unit}
                            size="sm"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
};

export default KPIDetail;
